import { CSSProperties } from 'react';
import { twJoin } from 'tailwind-merge';

import { Stats } from './StudentProgressStats.hooks';
import { bodyText, bodyTextSmall, subheading2 } from '@/styles/styles';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';

type Props = {
  stats: Stats;
};

export function StudentProgressStats(props: Props) {
  const {
    stats: {
      completed,
      total,
      percentage,
      className,
      icon,
      title,
      description,
    },
  } = props;

  const percentageValue = percentage * 100;

  const radialProgressStyle = {
    '--value': percentageValue,
    '--thickness': '0.5rem',
    '--size': '3.25rem',
  } as CSSProperties;

  return (
    <ConditionalRenderer condition={total > 0}>
      <li
        className={twJoin(
          'flex flex-col gap-2 rounded-xl shadow-default p-2.5 min-h-[8.125rem] flex-1',
          className.base,
        )}
      >
        <section className="flex gap-2 justify-between items-center">
          <figure className={twJoin('w-10 h-10 rounded-full', className.icon)}>
            {icon}
          </figure>
          <div className="relative">
            <div
              className={twJoin(
                'radial-progress absolute inset-0',
                className.div,
              )}
              style={radialProgressStyle}
              aria-hidden="true"
            />
            <div
              className={twJoin(
                'radial-progress relative text-[0.625rem]',
                className.progress,
              )}
              style={radialProgressStyle}
              aria-valuenow={percentage}
              role="progressbar"
            >
              {percentageValue}%
            </div>
          </div>
        </section>
        <span className="flex flex-col">
          <h2 className={twJoin(subheading2, 'text-base-content font-500')}>
            {completed}
          </h2>
          <p
            className={twJoin(
              bodyText,
              'text-base-content/80 font-500 leading-none',
            )}
          >
            {title}
          </p>
          <p
            className={twJoin(
              bodyTextSmall,
              'text-base-content/80 lowercase leading-none',
            )}
          >
            {description(total)}
          </p>
        </span>
      </li>
    </ConditionalRenderer>
  );
}
